import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { ICustomerSearchResult } from "../../../cashdesk/models/cashdeskModels";
import {
  giftVoucherRedeemFormDefaultValues,
  giftVoucherRedeemFormSchema,
  IGiftVoucherRedeemFormModel,
} from "../models/giftVoucherFormModel";

interface IUseGiftVoucherRedeemDialogProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * The "Осребри ваучер" dialog: scan or type a code, pick the client holding it, confirm.
 *
 * This records WHO is holding the voucher. It deliberately moves no money: the balance
 * stays on the voucher and stays spendable only at this studio. Crediting the client's
 * deposit instead would make it spendable across the whole connected studio group and
 * would double-count in the cash drawer.
 */
export const useGiftVoucherRedeemDialog = ({
  visible,
  onHide,
}: IUseGiftVoucherRedeemDialogProps) => {
  const [isScannerVisible, setIsScannerVisible] = useState(false);

  const form = useForm<IGiftVoucherRedeemFormModel>({
    defaultValues: giftVoucherRedeemFormDefaultValues,
    resolver: yupResolver(giftVoucherRedeemFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!visible) {
      form.reset(giftVoucherRedeemFormDefaultValues);
      setIsScannerVisible(false);
    }
  }, [visible, form]);

  // The same client list the cashdesk uses, so the two screens can never disagree about
  // who counts as a client of this studio.
  const { data: customers, isLoading: areCustomersLoading } = useApiQuery<
    ICustomerSearchResult[]
  >({
    queryKey: [endpointsMetadata.cashdesk.searchCustomers],
    endpoint: `${endpointsMetadata.cashdesk.searchCustomers}?query=`,
    enabled: visible,
  });

  const customerOptions: ISelectFieldModel[] = useMemo(
    () =>
      (customers ?? []).map((customer) => ({
        id: customer.id,
        name: `${customer.firstName} ${customer.lastName}${
          customer.phoneNumber ? ` (${customer.phoneNumber})` : ""
        }`,
      })),
    [customers],
  );

  /**
   * A scan fills the field rather than submitting: the staff member still has to pick the
   * client, and a code that scans cleanly can still belong to another studio — that is the
   * server's call (32005), not the scanner's.
   */
  const handleScanned = useCallback(
    (value: string) => {
      form.setValue("code", value.trim().toUpperCase(), {
        shouldValidate: true,
        shouldDirty: true,
      });
      setIsScannerVisible(false);
    },
    [form],
  );

  const { mutate, isLoading } = useApiMutation();

  const handleSubmit = form.handleSubmit((values: IGiftVoucherRedeemFormModel) => {
    mutate(
      {
        endpoint: endpointsMetadata.giftVoucher.assignHolder,
        method: "post",
        globalIsLoading: true,
        successMessage: "Ваучерът е свързан с клиента.",
        payload: {
          code: values.code,
          userId: values.userId,
        },
        invalidateKeys: [[endpointsMetadata.giftVoucher.getAll]],
      },
      {
        onSuccess: () => onHide(),
      },
    );
  });

  return {
    form,
    isLoading,
    customerOptions,
    areCustomersLoading,
    isScannerVisible,
    openScanner: () => setIsScannerVisible(true),
    closeScanner: () => setIsScannerVisible(false),
    handleScanned,
    handleSubmit,
  };
};
